import Phaser from 'phaser';
import { IsoGrid } from '@/iso/IsoGrid';
import { IsoCamera } from '@/iso/IsoCamera';
import { EffectsManager } from '@/effects/EffectsManager';
import { SimulationManager } from '@/simulation/SimulationManager';
import { SpecialCharacterManager } from '@/simulation/SpecialCharacterManager';
import { GRID_COLS, GRID_ROWS, TILE_WIDTH, TILE_HEIGHT, EquipmentType, EQUIPMENT_DEFS } from '@/config';
import { EventBus, GameEvent } from '@/utils/EventBus';
import { euclideanDistance } from '@/utils/GridUtils';
import { inverseSquareFalloff } from '@/utils/MathUtils';
import { MusicEngine } from '@/audio/MusicEngine';

export interface GameStateSnapshot {
  equipment: { type: EquipmentType; col: number; row: number }[];
  ravers: { col: number; row: number; colorIndex: number; state: string }[];
  specialCharacters: { name: string; col: number; row: number; speechText: string | null }[];
  beatCount: number;
}

/** Module-level snapshot for FPSScene to read */
export let fpsGameState: GameStateSnapshot | null = null;

export interface PlacedEquipment {
  id: string;
  type: EquipmentType;
  col: number;
  row: number;
  sprite: Phaser.GameObjects.Image;
}

export class MainScene extends Phaser.Scene {
  grid!: IsoGrid;
  private isoCamera!: IsoCamera;
  private effectsManager!: EffectsManager;
  private simulationManager!: SimulationManager;
  private specialCharacterManager!: SpecialCharacterManager;
  private floorTiles: Phaser.GameObjects.Image[] = [];
  private hoverHighlight!: Phaser.GameObjects.Image;
  private ghostPreview: Phaser.GameObjects.Image | null = null;
  private aoeHighlights: Phaser.GameObjects.Image[] = [];
  private equipmentSprites: Map<string, PlacedEquipment> = new Map();
  private nextEquipmentId = 0;

  // Build mode state
  private buildMode: EquipmentType | null = null;
  private demolishMode = false;

  // Music engine
  private musicEngine = new MusicEngine();
  private musicStarted = false;

  // FPS mode flag — MainScene stays running, just hidden under Three.js canvas
  private fpsActive = false;

  // Easter egg key sequences
  private readonly benSequence = 'benbenben';
  private benBuffer = '';
  private readonly pewpewSequence = 'pewpew';
  private pewpewBuffer = '';

  constructor() {
    super({ key: 'MainScene' });
  }

  create(): void {
    this.grid = new IsoGrid(GRID_COLS, GRID_ROWS, TILE_WIDTH, TILE_HEIGHT);
    this.isoCamera = new IsoCamera(this);
    this.effectsManager = new EffectsManager(this);
    this.simulationManager = new SimulationManager(this, this.grid);
    this.specialCharacterManager = new SpecialCharacterManager(this, this.grid);

    this.renderFloorGrid();
    this.setupHoverHighlight();
    this.setupInputHandlers();
    this.setupEventListeners();

    // Center camera on grid center
    const center = this.grid.gridToWorld(GRID_COLS / 2, GRID_ROWS / 2);
    this.isoCamera.centerOn(center.x, center.y);

    // Wire mute toggle from UIScene
    EventBus.on(GameEvent.TOGGLE_MUTE, () => {
      const muted = this.musicEngine.toggleMute();
      EventBus.emit(GameEvent.MUTE_CHANGED, muted);
    });

    // Update speaker count → volume when equipment changes
    EventBus.on(GameEvent.EQUIPMENT_PLACED, () => this.updateSpeakerVolume());
    EventBus.on(GameEvent.EQUIPMENT_REMOVED, () => this.updateSpeakerVolume());

    // Equipment movement (e.g. DJ booth fleeing from James)
    EventBus.on(GameEvent.EQUIPMENT_MOVED, (data: { type: EquipmentType; fromCol: number; fromRow: number; toCol: number; toRow: number }) => {
      this.moveEquipment(data);
    });

    // Easter egg music control
    EventBus.on(GameEvent.MUSIC_STOP, () => this.musicEngine.suspend());
    EventBus.on(GameEvent.MUSIC_RESUME, () => {
      if (this.musicStarted) this.musicEngine.resume();
    });

    // FPS mode exit — re-enable 2D input
    EventBus.on(GameEvent.FPS_MODE_EXITED, () => {
      this.fpsActive = false;
    });

    // Easter eggs: key sequence detection
    this.input.keyboard!.on('keydown', (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key.length === 1 && key >= 'a' && key <= 'z') {
        // BEN easter egg
        this.benBuffer += key;
        if (this.benBuffer.length > this.benSequence.length) {
          this.benBuffer = this.benBuffer.slice(-this.benSequence.length);
        }
        if (this.benBuffer === this.benSequence) {
          this.benBuffer = '';
          this.scene.stop('MainScene');
          this.scene.stop('UIScene');
          this.scene.start('BenScene');
        }

        // PEWPEW easter egg — first-person 3D rave mode
        this.pewpewBuffer += key;
        if (this.pewpewBuffer.length > this.pewpewSequence.length) {
          this.pewpewBuffer = this.pewpewBuffer.slice(-this.pewpewSequence.length);
        }
        if (this.pewpewBuffer === this.pewpewSequence) {
          this.pewpewBuffer = '';
          fpsGameState = this.getGameState();
          this.fpsActive = true;
          // Keep MainScene + UIScene running (sim continues), launch FPS on top
          this.scene.launch('FPSScene');
        }
      }
    });
  }

  update(_time: number, delta: number): void {
    // Always tick simulation and special characters (even under FPS overlay)
    this.simulationManager.update(delta);
    this.specialCharacterManager.update(delta,
      this.getPlacedEquipment().map((e) => ({ type: e.type, col: e.col, row: e.row }))
    );

    // Continuously update snapshot for FPS scene to read
    if (this.fpsActive) {
      fpsGameState = this.getGameState();
      return; // Skip camera/effects/input — hidden under Three.js canvas
    }

    this.isoCamera.update();
    this.effectsManager.update(delta, () =>
      this.getPlacedEquipment().map((e) => {
        const world = this.grid.gridToWorld(e.col, e.row);
        return { ...e, worldX: world.x, worldY: world.y };
      })
    );
  }

  private renderFloorGrid(): void {
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        const world = this.grid.gridToWorld(col, row);
        const tile = this.add.image(world.x, world.y, 'floor-tile');
        tile.setDepth(0);
        this.floorTiles.push(tile);
      }
    }
  }

  private setupHoverHighlight(): void {
    this.hoverHighlight = this.add.image(0, 0, 'highlight-hover');
    this.hoverHighlight.setAlpha(0.5);
    this.hoverHighlight.setDepth(1);
    this.hoverHighlight.setVisible(false);
  }

  private setupInputHandlers(): void {
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.fpsActive) return;
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const gridPos = this.grid.worldToGrid(worldPoint.x, worldPoint.y);

      if (this.grid.isInBounds(gridPos.col, gridPos.row)) {
        const tileWorld = this.grid.gridToWorld(gridPos.col, gridPos.row);
        this.hoverHighlight.setPosition(tileWorld.x, tileWorld.y);
        this.hoverHighlight.setVisible(true);

        if (this.buildMode) {
          this.updateGhostPreview(gridPos.col, gridPos.row);
        }
      } else {
        this.hoverHighlight.setVisible(false);
        if (this.ghostPreview) this.ghostPreview.setVisible(false);
        this.clearAoeHighlights();
      }
    });

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.fpsActive) return;
      if (pointer.rightButtonDown()) return;

      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const gridPos = this.grid.worldToGrid(worldPoint.x, worldPoint.y);

      if (!this.grid.isInBounds(gridPos.col, gridPos.row)) return;

      if (this.demolishMode) {
        this.demolishAt(gridPos.col, gridPos.row);
      } else if (this.buildMode) {
        this.placeEquipment(this.buildMode, gridPos.col, gridPos.row);
      }
    });
  }

  private setupEventListeners(): void {
    EventBus.on(GameEvent.BUILD_MODE_CHANGED, (mode: { type: EquipmentType | null; demolish: boolean }) => {
      this.buildMode = mode.type;
      this.demolishMode = mode.demolish;

      // Clean up ghost preview and AoE when exiting build mode
      if (!this.buildMode && this.ghostPreview) {
        this.ghostPreview.destroy();
        this.ghostPreview = null;
      }
      this.clearAoeHighlights();

      // Update highlight color
      if (this.demolishMode) {
        this.hoverHighlight.setTexture('highlight-invalid');
      } else {
        this.hoverHighlight.setTexture('highlight-hover');
      }
    });
  }

  private updateGhostPreview(col: number, row: number): void {
    if (!this.buildMode) return;

    const textureKey = `equipment-${this.buildMode}`;
    if (!this.ghostPreview) {
      this.ghostPreview = this.add.image(0, 0, textureKey);
      this.ghostPreview.setAlpha(0.6);
      this.ghostPreview.setDepth(999);
    } else {
      this.ghostPreview.setTexture(textureKey);
    }

    const world = this.grid.gridToWorld(col, row);
    this.ghostPreview.setPosition(world.x, world.y);
    this.ghostPreview.setVisible(true);

    // Color based on validity
    const canPlace = !this.grid.isCellOccupied(col, row);
    this.ghostPreview.setTint(canPlace ? 0x00ff00 : 0xff0000);
    this.hoverHighlight.setTexture(canPlace ? 'highlight-valid' : 'highlight-invalid');

    // Show AoE radius
    this.updateAoeHighlights(col, row, this.buildMode);
  }

  private updateAoeHighlights(centerCol: number, centerRow: number, type: EquipmentType): void {
    const def = EQUIPMENT_DEFS[type];
    const radius = def.radius;

    // Hide all existing highlights first
    this.clearAoeHighlights();

    if (radius <= 0) return;

    const color = def.color;
    const radiusCeil = Math.ceil(radius);
    let spriteIdx = 0;

    for (let row = centerRow - radiusCeil; row <= centerRow + radiusCeil; row++) {
      for (let col = centerCol - radiusCeil; col <= centerCol + radiusCeil; col++) {
        if (!this.grid.isInBounds(col, row)) continue;
        if (col === centerCol && row === centerRow) continue;

        const dist = euclideanDistance(col, row, centerCol, centerRow);
        const intensity = inverseSquareFalloff(dist, radius);
        if (intensity <= 0) continue;

        // Reuse existing sprite or create new one
        let highlight: Phaser.GameObjects.Image;
        if (spriteIdx < this.aoeHighlights.length) {
          highlight = this.aoeHighlights[spriteIdx];
          highlight.setVisible(true);
        } else {
          highlight = this.add.image(0, 0, 'highlight-aoe');
          highlight.setDepth(2);
          this.aoeHighlights.push(highlight);
        }

        const tileWorld = this.grid.gridToWorld(col, row);
        highlight.setPosition(tileWorld.x, tileWorld.y);
        highlight.setAlpha(intensity * 0.5);
        highlight.setTint(color);
        spriteIdx++;
      }
    }

    // Hide remaining unused sprites
    for (let i = spriteIdx; i < this.aoeHighlights.length; i++) {
      this.aoeHighlights[i].setVisible(false);
    }
  }

  private clearAoeHighlights(): void {
    for (const h of this.aoeHighlights) {
      h.setVisible(false);
    }
  }

  placeEquipment(type: EquipmentType, col: number, row: number): boolean {
    if (this.grid.isCellOccupied(col, row)) return false;

    const def = EQUIPMENT_DEFS[type];
    if (!this.simulationManager.economy.canAfford(def.cost)) return false;
    const id = `eq-${this.nextEquipmentId++}`;
    const world = this.grid.gridToWorld(col, row);
    const textureKey = `equipment-${type}`;

    const sprite = this.add.image(world.x, world.y, textureKey);
    sprite.setDepth(world.y + 10); // depth sort by Y

    this.grid.setCellOccupied(col, row, id);
    const placed: PlacedEquipment = { id, type, col, row, sprite };
    this.equipmentSprites.set(id, placed);

    EventBus.emit(GameEvent.EQUIPMENT_PLACED, { id, type, col, row, cost: def.cost });

    // Start music when first speaker is placed
    if (type === EquipmentType.SPEAKER && !this.musicStarted) {
      this.musicEngine.start();
      this.musicStarted = true;
    }

    return true;
  }

  private demolishAt(col: number, row: number): void {
    const id = this.grid.getCellContent(col, row);
    if (!id) return;

    const equipment = this.equipmentSprites.get(id);
    if (!equipment) return;

    equipment.sprite.destroy();
    this.equipmentSprites.delete(id);
    this.grid.clearCell(col, row);

    const def = EQUIPMENT_DEFS[equipment.type];
    EventBus.emit(GameEvent.EQUIPMENT_REMOVED, {
      id,
      type: equipment.type,
      col,
      row,
      refund: Math.floor(def.cost * 0.5),
    });
  }

  private moveEquipment(data: { type: EquipmentType; fromCol: number; fromRow: number; toCol: number; toRow: number }): void {
    const id = this.grid.getCellContent(data.fromCol, data.fromRow);
    if (!id) return;

    const equipment = this.equipmentSprites.get(id);
    if (!equipment || equipment.type !== data.type) return;

    // Check destination is valid and unoccupied
    if (!this.grid.isInBounds(data.toCol, data.toRow)) return;
    if (this.grid.isCellOccupied(data.toCol, data.toRow)) return;

    // Update grid occupancy
    this.grid.clearCell(data.fromCol, data.fromRow);
    this.grid.setCellOccupied(data.toCol, data.toRow, id);

    // Update stored position
    equipment.col = data.toCol;
    equipment.row = data.toRow;

    // Animate sprite to new position
    const world = this.grid.gridToWorld(data.toCol, data.toRow);
    this.tweens.add({
      targets: equipment.sprite,
      x: world.x,
      y: world.y,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        equipment.sprite.setDepth(world.y + 10);
      },
    });
  }

  private updateSpeakerVolume(): void {
    const count = this.getEquipmentByType(EquipmentType.SPEAKER).length;
    this.musicEngine.setSpeakerCount(count);
  }

  getPlacedEquipment(): PlacedEquipment[] {
    return Array.from(this.equipmentSprites.values());
  }

  getEquipmentByType(type: EquipmentType): PlacedEquipment[] {
    return this.getPlacedEquipment().filter((e) => e.type === type);
  }

  private getGameState(): GameStateSnapshot {
    const equipment = this.getPlacedEquipment().map((e) => ({
      type: e.type,
      col: e.col,
      row: e.row,
    }));

    const ravers: GameStateSnapshot['ravers'] = [];
    for (const raver of this.simulationManager.crowdManager.getRavers()) {
      ravers.push({
        col: raver.col,
        row: raver.row,
        colorIndex: raver.colorIndex,
        state: raver.state,
      });
    }

    const specialCharacters: GameStateSnapshot['specialCharacters'] = [];
    for (const char of this.specialCharacterManager.getCharacters()) {
      specialCharacters.push({
        name: char.name,
        col: char.col,
        row: char.row,
        speechText: char.speechText,
      });
    }

    return {
      equipment,
      ravers,
      specialCharacters,
      beatCount: this.effectsManager.beatSync.beatCount,
    };
  }
}
